import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'

import { Grid, TextField } from '@mui/material'

import { OrganizationType, organizationSchema } from '@/api/organizations'
import {
    fetchOrganizationQueryOptions,
    useUpdateOrganizationMutation
} from '@/api/organizationsQueryOptions'
import FormDebug from '@/components/FormDebug'
import LoadProgress from '@/components/LoadProgress'
import PostReceiverPanel from '@/components/org/PostReceiverPanel'
import { t } from 'i18next'
import { enqueueSnackbar } from 'notistack'

export const Route = createFileRoute(
    '/_auth/organization_/$organizationUuid/main',
)({
    component: OrganizationComponent,
})

function OrganizationComponent() {
    const params = Route.useParams()
    const organizationQuery = useSuspenseQuery(
        fetchOrganizationQueryOptions(params.organizationUuid),
    )
    const organization = organizationQuery.data

    const updateOrganizationMutation = useUpdateOrganizationMutation()

    const {
        register,
        handleSubmit,
        formState: { isValid, isDirty, errors },
    } = useForm<OrganizationType>({
        resolver: yupResolver<OrganizationType, any, any>(organizationSchema),
        mode: 'onBlur',
        shouldFocusError: true,
    })

    const onSubmit = (data: OrganizationType) => {
        if (isValid) {
            updateOrganizationMutation.mutate({ ...organization, ...data })
            enqueueSnackbar(t('action.changed', { entity: t('entity.organization') }), {
                variant: 'success',
            })
        } else {
            console.log(errors)
        }
    }

    const onError = () => {
        console.log('error')
    }

    if (organizationQuery.isLoading || organizationQuery.isFetching || !organization) {
        return <LoadProgress />
    }

    const writeable = organization.permissions
        ? organization.permissions.some((perm) => perm === 'write')
        : false

    return (
        (<>
            <form onBlur={handleSubmit(onSubmit, onError)}>
                <div>
                    <Grid container spacing={1}>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 6
                            }}>
                            <input
                                type="hidden"
                                {...register('uuid')}
                                defaultValue={organization.uuid}
                            />
                            <input
                                type="hidden"
                                {...register('parent.uuid')}
                                defaultValue={organization.parent?.uuid}
                            />
                            <TextField
                                defaultValue={organization.name}
                                {...register('name')}
                                disabled={!writeable}
                                required={true}
                                label="Name"
                                helperText={errors.name?.message}
                            />
                        </Grid>
                        <Grid
                            size={{
                                xs: 6,
                                md: 3,
                                lg: 2
                            }}>
                            <TextField
                                defaultValue={organization.abbreviation}
                                {...register('abbreviation')}
                                disabled={!writeable}
                                required={true}
                                label="Abkürzung"
                                helperText={errors.abbreviation?.message}
                            />
                        </Grid>
                        <Grid
                            size={{
                                xs: 6,
                                md: 3,
                                lg: 2
                            }}>
                            <TextField
                                defaultValue={organization.medusaAbbreviation}
                                {...register('medusaAbbreviation')}
                                disabled={!writeable}
                                label="Medusa-Abkürzung"
                                helperText={errors.medusaAbbreviation?.message}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                        <Grid
                            size={{
                                xs: 6,
                                md: 3,
                                lg: 2
                            }}>
                            <TextField
                                defaultValue={organization.color}
                                {...register('color')}
                                disabled={!writeable}
                                label="Farbe"
                                helperText={errors.color?.message || t('helperText.organization.color')}
                            />
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 6
                            }}>
                            <TextField
                                defaultValue={organization.nameAffix}
                                {...register('nameAffix')}
                                disabled={!writeable}
                                label="Namenszusatz"
                                helperText={errors.nameAffix?.message}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                        <Grid
                            size={{
                                xs: 6,
                                md: 3,
                                lg: 2
                            }}>
                            <TextField
                                defaultValue={organization.foundationYear}
                                {...register('foundationYear')}
                                disabled={!writeable}
                                label={t('property.organization.foundationYear')}
                                helperText={errors.foundationYear?.message}
                            />
                        </Grid>
                        <Grid
                            size={{
                                xs: 6,
                                md: 3,
                                lg: 2
                            }}>
                            <TextField
                                defaultValue={organization.dissolvedYear}
                                {...register('dissolvedYear')}
                                disabled={!writeable}
                                label={t('property.organization.dissolvedYear')}
                                helperText={errors.dissolvedYear?.message}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                        <Grid
                            size={{
                                xs: 6,
                                md: 3,
                                lg: 4
                            }}>
                            <TextField
                                defaultValue={organization.street}
                                {...register('street')}
                                disabled={!writeable}
                                label={t('property.organization.street')}
                                helperText={errors.street?.message}
                            />
                        </Grid>
                        <Grid
                            size={{
                                xs: 6,
                                md: 3,
                                lg: 2
                            }}>
                            <TextField
                                defaultValue={organization.postcode}
                                {...register('postcode')}
                                disabled={!writeable}
                                label={t('property.organization.postcode')}
                                helperText={errors.postcode?.message}
                            />
                        </Grid>
                        <Grid
                            size={{
                                xs: 6,
                                md: 3,
                                lg: 2
                            }}>
                            <TextField
                                defaultValue={organization.city}
                                {...register('city')}
                                disabled={!writeable}
                                label={t('property.organization.city')}
                                helperText={errors.city?.message}
                            />
                        </Grid>
                    </Grid>

                    <Grid container spacing={1}>
                        <Grid
                            size={{
                                xs: 6,
                                md: 3,
                                lg: 4
                            }}>
                            <TextField
                                defaultValue={organization.latitude}
                                {...register('latitude')}
                                disabled={!writeable}
                                label={t('property.organization.latitude')}
                                helperText={errors.latitude?.message}
                            />
                        </Grid>
                        <Grid
                            size={{
                                xs: 6,
                                md: 3,
                                lg: 4
                            }}>
                            <TextField
                                defaultValue={organization.longitude}
                                {...register('longitude')}
                                disabled={!writeable}
                                label={t('property.organization.longitude')}
                                helperText={errors.longitude?.message}
                            />
                        </Grid>
                    </Grid>

                    <Grid container spacing={1}>
                        <Grid
                            size={{
                                xs: 6,
                                md: 3,
                                lg: 4
                            }}>
                            <TextField
                                defaultValue={organization.website}
                                {...register('website')}
                                disabled={!writeable}
                                label={t('property.organization.website')}
                                helperText={errors.website?.message}
                            />
                        </Grid>
                        <Grid
                            size={{
                                xs: 6,
                                md: 3,
                                lg: 4
                            }}>
                            <TextField
                                defaultValue={organization.emailPrimary}
                                {...register('emailPrimary')}
                                disabled={!writeable}
                                label={t('property.organization.emailPrimary')}
                                helperText={errors.emailPrimary?.message}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                        <Grid
                            size={{
                                xs: 6,
                                md: 3,
                                lg: 4
                            }}>
                            <TextField
                                defaultValue={organization.zvr}
                                {...register('zvr')}
                                disabled={!writeable}
                                label={t('property.organization.zvr')}
                                helperText={errors.zvr?.message}
                            />
                        </Grid>
                        <Grid
                            size={{
                                xs: 6,
                                md: 3,
                                lg: 4
                            }}>
                            <TextField
                                defaultValue={organization.uid}
                                {...register('uid')}
                                disabled={!writeable}
                                label={t('property.organization.uid')}
                                helperText={errors.uid?.message}
                            />
                        </Grid>
                    </Grid>
                    <FormDebug
                        errors={errors}
                        dirty={isDirty}
                        valid={isValid}
                        value={organization}
                    />
                </div>
            </form>
            <PostReceiverPanel organizationUuid={params.organizationUuid} />
        </>)
    );
}
